import { useGetSalesExpertsListQuery } from '@/services';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useSnackbar } from '@/hooks';
import type { ExclusionUpdate, SalesExpert } from '@/types/user-management.types';

import { LoadingTable } from '../../../../components';

import { getActualChanges, getFilteredExperts, getUpdatedExperts } from '../../user-management.utils';
import { SalesExpertsTabProps } from './SalesExpertsTab.types';
import { MarketersTable } from '../MarketersTable';

export const SalesExpertsTab: React.FC<SalesExpertsTabProps> = props => {
  const { searchQuery, activeTab, bodyRef, onExclusionUpdatesChange, resetRequestId } = props as SalesExpertsTabProps;
  const { setSnackbarMessage } = useSnackbar();
  const {
    data: salesExperts = [],
    isSuccess,
    isLoading,
    isError
  } = useGetSalesExpertsListQuery(undefined, { skip: activeTab !== 'marketers', refetchOnMountOrArgChange: true });

  const originalSalesExpertsRef = useRef<SalesExpert[]>([]);

  const [currentSalesExpert, setCurrentSalesExpert] = useState<SalesExpert | null>(null);
  const [exclusionUpdates, setExclusionUpdates] = useState<ExclusionUpdate[]>([]);
  const [localSalesExperts, setLocalSalesExperts] = useState<SalesExpert[]>([]);

  useEffect(() => {
    if (isError) {
      setSnackbarMessage('Помилка завантаження маркетологів', 'error');
    }
  }, [isError, setSnackbarMessage]);

  useEffect(() => {
    if (!isSuccess) return;

    setLocalSalesExperts(salesExperts);
    originalSalesExpertsRef.current = JSON.parse(JSON.stringify(salesExperts));
  }, [isSuccess, salesExperts]);

  useEffect(() => {
    if (onExclusionUpdatesChange) {
      onExclusionUpdatesChange(exclusionUpdates);
    }
  }, [exclusionUpdates, onExclusionUpdatesChange]);

  useEffect(() => {
    if (!currentSalesExpert) return;
    const latest = localSalesExperts.find(e => e.expert_email === currentSalesExpert.expert_email) || null;
    if (latest && latest !== currentSalesExpert) {
      setCurrentSalesExpert(latest);
    }
  }, [localSalesExperts, currentSalesExpert]);

  const previousResetRequestId = useRef<number | undefined>(undefined);

  const resetExclusions = useCallback(() => {
    const restored = JSON.parse(JSON.stringify(originalSalesExpertsRef.current));
    setLocalSalesExperts(restored);
    setExclusionUpdates([]);

    setCurrentSalesExpert(prev =>
      prev ? (restored.find((e: SalesExpert) => e.expert_email === prev.expert_email) as SalesExpert) || null : null
    );
  }, []);

  useEffect(() => {
    if (resetRequestId === undefined) return;
    if (previousResetRequestId.current === undefined) {
      previousResetRequestId.current = resetRequestId;
      return;
    }
    if (resetRequestId !== previousResetRequestId.current) {
      resetExclusions();
      previousResetRequestId.current = resetRequestId;
    }
  }, [resetRequestId, resetExclusions]);

  const FilteredSalesExperts = useMemo(
    () => getFilteredExperts(localSalesExperts, searchQuery),
    [localSalesExperts, searchQuery]
  );

  const toggleExpertExpansion = useCallback(
    (expertEmail: string) => {
      setCurrentSalesExpert(prev =>
        prev?.expert_email === expertEmail
          ? null
          : localSalesExperts.find(expert => expert.expert_email === expertEmail) || null
      );
    },
    [localSalesExperts]
  );

  const handleLocalExclusionChange = useCallback(
    (expertEmail: string, stationUnicode: number, active: boolean) => {
      const updatedExperts = getUpdatedExperts(localSalesExperts, expertEmail, stationUnicode, active);

      const updatedCurrentExpert =
        updatedExperts.find(expert => expert.expert_email === currentSalesExpert?.expert_email) || null;

      setLocalSalesExperts(updatedExperts);
      setCurrentSalesExpert(updatedCurrentExpert);
      setExclusionUpdates(getActualChanges(updatedExperts, originalSalesExpertsRef.current));
    },
    [localSalesExperts, currentSalesExpert]
  );

  if (activeTab !== 'marketers') {
    return <></>;
  }

  return (
    <div className="user-management-results-container">
      {isLoading ? (
        <LoadingTable message="Завантаження маркетологів..." rows={8} columns={6} />
      ) : (
        <MarketersTable
          experts={FilteredSalesExperts}
          currentExpert={currentSalesExpert}
          onToggleExpansion={toggleExpertExpansion}
          onExclusionChange={handleLocalExclusionChange}
          bodyRef={bodyRef}
        />
      )}
    </div>
  );
};
